import { faStar as faRegularStar } from "@fortawesome/free-regular-svg-icons";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useTheme,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  Typography,
  TextField,
  FormControl,
  RadioGroup,
  Grid,
  FormControlLabel,
  Radio,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { StyledTable } from "./styles";
import { EmployeeSurveyDTO } from "../../models/employee-survey";
import { EmployeeSurveyConfigurationPredefinedAnswerRef } from "../../models/employee-survey-configuration-predefined-answer/employee-survey-configuration-predefined-answer-ref";
import { EmployeeSurveyItemRef } from "../../models/employee-survey-items/employee-survey-item-ref";
import getBoolean from "../../utils/bool-util";
import { generateArrOfNumbers } from "../../utils/generate-arr-of-numbers";

interface Props {
  readOnly?: boolean;
  survey: EmployeeSurveyDTO;
  handleChangeSurveyItem?: (
    surveyItem: EmployeeSurveyItemRef,
    surveyItemIdx: number,
    field: keyof EmployeeSurveyItemRef,
    value: EmployeeSurveyItemRef[keyof EmployeeSurveyItemRef]
  ) => void;
}

const EmployeeSurveyContent = ({ readOnly = false, survey, handleChangeSurveyItem }: Props) => {
  const tRP = "components.edit_employee_survey";
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <>
      <Alert
        severity="info"
        style={{ margin: "15px 0px" }}
      >
        {survey.employeeSurveyConfiguration?.surveyExplanation}
      </Alert>
      <StyledTable>
        <TableHead>
          <TableRow>
            <TableCell align="center">{`${t(`${tRP}.fields.question`)}`}</TableCell>
            <TableCell
              align="center"
              style={{ width: "60%" }}
            >{`${t(`${tRP}.fields.answer`)}`}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {survey.surveyItems
            .sort((a, b) => (a.item?.sequence ?? 0) - (b.item?.sequence ?? 0))
            .map((surveyItem, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <Grid style={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography
                      style={{
                        width: "calc(100% - 15px)",
                      }}
                    >{`${idx + 1}. ${surveyItem.item?.text}`}</Typography>
                    {surveyItem.item?.isRequired && (
                      <Tooltip
                        title={`${t(`${tRP}.required_field`)}`}
                        style={{ color: theme.palette.error.light }}
                      >
                        <Typography variant="h5">*</Typography>
                      </Tooltip>
                    )}
                  </Grid>
                </TableCell>
                <TableCell>
                  {surveyItem.item && surveyItem.item.type?.maxTextLength ? (
                    !readOnly && handleChangeSurveyItem ? (
                      <TextField
                        fullWidth
                        value={surveyItem.textAnswer}
                        onChange={(e) => handleChangeSurveyItem(surveyItem, idx, "textAnswer", e.target.value)}
                        inputProps={{
                          maxLength: surveyItem.item.maxTextLength > 0 ? surveyItem.item.maxTextLength : null,
                        }}
                        helperText={
                          surveyItem.item.maxTextLength && surveyItem.item.maxTextLength > 0
                            ? `${t(`${tRP}.max_length_message`, { maxTextLength: surveyItem.item.maxTextLength })}`
                            : ""
                        }
                        multiline
                        maxRows={5}
                      />
                    ) : surveyItem.textAnswer && surveyItem.textAnswer.trim().length > 0 ? (
                      <Typography>{surveyItem.textAnswer}</Typography>
                    ) : (
                      <Typography>
                        <i> No answer</i>
                      </Typography>
                    )
                  ) : surveyItem.item?.type?.listOfAnswers ? (
                    <FormControl>
                      <RadioGroup value={surveyItem.predefinedAnswer?.id ?? null}>
                        <Grid style={{ display: "flex", gap: "10px", flexDirection: "column" }}>
                          {surveyItem.item.predefinedAnswers
                            .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
                            .map((predefinedAnswer, idx) => (
                              <FormControlLabel
                                key={idx}
                                value={predefinedAnswer.id}
                                control={
                                  <Radio
                                    disabled={readOnly}
                                    style={{
                                      color: theme.palette.text.primary,
                                      cursor: readOnly ? "default" : "pointer",
                                    }}
                                    onClick={() => {
                                      let answer: EmployeeSurveyConfigurationPredefinedAnswerRef | null = null;

                                      predefinedAnswer.id === surveyItem.predefinedAnswer?.id
                                        ? (answer = null)
                                        : (answer = predefinedAnswer);

                                      !readOnly &&
                                        handleChangeSurveyItem &&
                                        handleChangeSurveyItem(surveyItem, idx, "predefinedAnswer", answer);
                                    }}
                                  />
                                }
                                label={predefinedAnswer.name}
                                checked={surveyItem.predefinedAnswer?.id === predefinedAnswer.id}
                              />
                            ))}
                        </Grid>
                      </RadioGroup>
                    </FormControl>
                  ) : surveyItem.item?.type?.polarQuestion ? (
                    <FormControl style={{ width: "100%" }}>
                      <RadioGroup>
                        <Grid style={{ display: "flex", gap: "10px" }}>
                          {["true", "false"].map((polarAnswer, idx) => (
                            <FormControlLabel
                              key={idx}
                              value={polarAnswer}
                              control={
                                <Radio
                                  disabled={readOnly}
                                  style={{
                                    color: theme.palette.text.primary,
                                    cursor: readOnly ? "default" : "pointer",
                                  }}
                                  onClick={() => {
                                    let answer: boolean | null = null;

                                    (polarAnswer === "true" && surveyItem.polarAnswer === true) ||
                                    (polarAnswer === "false" && surveyItem.polarAnswer === false)
                                      ? (answer = null)
                                      : (answer = polarAnswer === "true");

                                    !readOnly &&
                                      handleChangeSurveyItem &&
                                      handleChangeSurveyItem(surveyItem, idx, "polarAnswer", answer);
                                  }}
                                />
                              }
                              label={polarAnswer === "true" ? "Yes" : "No"}
                              checked={surveyItem.polarAnswer === getBoolean(polarAnswer)}
                            />
                          ))}
                        </Grid>
                      </RadioGroup>
                    </FormControl>
                  ) : surveyItem.item?.type?.ranking ? (
                    readOnly ? (
                      <Grid style={{ display: "flex", gap: "10px" }}>
                        {generateArrOfNumbers(surveyItem.item.rankingUpTo, 1).map((ranking: number, _idx) => (
                          <FontAwesomeIcon
                            key={_idx}
                            icon={surveyItem.ranking && surveyItem.ranking >= ranking ? faStar : faRegularStar}
                            color={theme.palette.text.primary}
                          />
                        ))}
                      </Grid>
                    ) : (
                      <Grid>
                        {generateArrOfNumbers(surveyItem.item.rankingUpTo, 1).map((ranking: number, _idx) => (
                          <IconButton
                            key={_idx}
                            onClick={() => {
                              const newRanking = ranking === 1 && surveyItem.ranking === 1 ? 0 : ranking;
                              handleChangeSurveyItem && handleChangeSurveyItem(surveyItem, idx, "ranking", newRanking);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={surveyItem.ranking && surveyItem.ranking >= ranking ? faStar : faRegularStar}
                              color={theme.palette.primary.main}
                            />
                          </IconButton>
                        ))}
                      </Grid>
                    )
                  ) : (
                    <></>
                  )}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </StyledTable>
    </>
  );
};

export default EmployeeSurveyContent;
